import { useState, useEffect } from "react";
import { Video, Play, Clock, Circle, CheckCircle, Loader2, ExternalLink } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Tutorial {
  id: string;
  title: string;
  duration: string;
  videoUrl: string | null;
  description: string;
  completed: boolean;
  affiliateUrl?: string;
}

const defaultTutorials: Tutorial[] = [
  {
    id: "nexis-ai",
    title: "Getting Started with Nexis AI",
    duration: "11:53",
    videoUrl: "https://drive.google.com/file/d/1Phsqkqu-3oqJrGiqqb5SscNRH8BK-3tM/preview",
    description: "Learn how to create viral scripts and story ideas that captivate your audience. This tutorial covers creating new story projects, generating script ideas with AI, choosing visual styles, setting scene counts, and generating prompts for your preferred AI image tool.",
    completed: false,
  },
  {
    id: "monetization-ai",
    title: "Getting Started with Monetization AI",
    duration: "20:57",
    videoUrl: "https://drive.google.com/file/d/129Hq24St8DBjM3wLaKiMC-tKBsEyjjdI/preview",
    description: "Turn your content into revenue-generating products like courses and eBooks. Learn to choose your product type, input your idea or paste a script, let AI generate your product structure, review and customize modules, then export and sell on your platform.",
    completed: false,
  },
  {
    id: "openart-ai",
    title: "OpenArt AI Tutorial",
    duration: "9:13",
    videoUrl: "https://drive.google.com/file/d/1d6UWdc-AxqwJSEfckgHSkFmOI8_GRngl/preview",
    description: "Master AI image generation for thumbnails, channel art, and stunning video assets. This tutorial covers signing up, pasting Nexis AI prompts, choosing AI models like Flux or SDXL, adjusting settings, and downloading your images.",
    completed: false,
    affiliateUrl: "https://openart.ai/home/?via=fint",
  },
  {
    id: "elevenlabs",
    title: "ElevenLabs Tutorial",
    duration: "4:35",
    videoUrl: "https://drive.google.com/file/d/1IB7ucdCsXyj9c6guKVsaKMMNuN1m-JFi/preview",
    description: "Create professional AI voiceovers for your videos with natural-sounding voices. Learn to create an account, choose voices, paste scripts, adjust voice settings for stability and clarity, generate previews, and download MP3s.",
    completed: false,
    affiliateUrl: "https://try.elevenlabs.io/tcc3g77xuya2",
  },
  {
    id: "hera-ai",
    title: "Hera AI Tutorial",
    duration: "4:01",
    videoUrl: "https://drive.google.com/file/d/1_Ngi3zs87g8VjRWr4P4OTQ8hwX_crRIf/preview",
    description: "Generate stunning motion graphics prompts for eye-catching visual effects. Learn to access Hera AI, describe motion effects, generate prompts, and apply effects to enhance your content.",
    completed: false,
    affiliateUrl: "https://hera.cello.so/EpChUOxiBIV",
  },
];

// Convert Google Drive URLs to embed format
const getEmbedUrl = (url: string | null): string | null => {
  if (!url) return null;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) {
    return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  }
  return url;
};

export default function Tutorials() {
  const { user } = useAuth();
  const [tutorials, setTutorials] = useState<Tutorial[]>(defaultTutorials);
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial>(defaultTutorials[0]);
  const [activeTab, setActiveTab] = useState("notes");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  // Load progress from database
  useEffect(() => {
    const loadProgress = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('lesson_progress')
          .select('lesson_id, completed')
          .eq('user_id', user.id);

        if (error) throw error;

        const progressMap = new Map(data?.map(p => [p.lesson_id, p.completed]) || []);
        const updatedTutorials = defaultTutorials.map(tutorial => ({
          ...tutorial,
          completed: progressMap.get(`tutorial-${tutorial.id}`) || false
        }));

        setTutorials(updatedTutorials);
        const firstIncomplete = updatedTutorials.find(t => !t.completed && t.videoUrl);
        setSelectedTutorial(firstIncomplete || updatedTutorials[0]);
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [user]);

  const toggleCompletion = async (tutorialId: string) => {
    if (!user) {
      toast.error('Please log in to track progress');
      return;
    }

    setIsUpdating(tutorialId);
    const tutorial = tutorials.find(t => t.id === tutorialId);
    const newCompleted = !tutorial?.completed;

    try {
      const { error } = await supabase
        .from('lesson_progress')
        .upsert({
          user_id: user.id,
          lesson_id: `tutorial-${tutorialId}`,
          completed: newCompleted,
          completed_at: newCompleted ? new Date().toISOString() : null
        }, {
          onConflict: 'user_id,lesson_id'
        });

      if (error) throw error;

      setTutorials(prev => prev.map(t =>
        t.id === tutorialId ? { ...t, completed: newCompleted } : t
      ));

      if (selectedTutorial.id === tutorialId) {
        setSelectedTutorial(prev => ({ ...prev, completed: newCompleted }));
      }

      toast.success(newCompleted ? 'Tutorial marked as complete!' : 'Tutorial marked as incomplete');
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    } finally {
      setIsUpdating(null);
    }
  };

  const completedCount = tutorials.filter(t => t.completed).length;
  const progressPercent = Math.round((completedCount / tutorials.length) * 100);
  const embedUrl = getEmbedUrl(selectedTutorial.videoUrl);

  if (isLoading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <Video className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-lg md:text-xl font-bold">Tutorials</h1>
            <p className="text-xs md:text-sm text-muted-foreground truncate">Learn how to get the most out of your AI tools</p>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 md:gap-6">
          {/* Video Player - Show first on mobile */}
          <div className="lg:col-span-8 lg:order-2 space-y-4">
            {/* Video Player Container */}
            <div className="bg-card/30 border border-border/50 rounded-xl overflow-hidden">
              <div className="aspect-video bg-black/50 relative">
                {embedUrl ? (
                  <iframe
                    src={embedUrl}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={selectedTutorial.title}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted/30 to-muted/10">
                    <div className="text-center">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-3">
                        <Play className="w-5 h-5 md:w-6 md:h-6 text-primary/40 ml-0.5" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-background/50 backdrop-blur-sm">
                        <Clock className="w-3 h-3 mr-1" />
                        Coming Soon
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tutorial Info */}
            <div className="bg-card/50 border border-border/50 rounded-xl p-4 md:p-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h2 className="font-display text-base md:text-lg font-semibold text-foreground mb-1 line-clamp-2">
                    {selectedTutorial.title}
                  </h2>
                  <p className="text-xs md:text-sm text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    {selectedTutorial.videoUrl ? selectedTutorial.duration : "Coming Soon"}
                  </p>
                </div>
                {selectedTutorial.videoUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleCompletion(selectedTutorial.id)}
                    disabled={isUpdating === selectedTutorial.id}
                    className={`shrink-0 text-xs md:text-sm ${selectedTutorial.completed ? "border-primary/50 text-primary" : ""}`}
                  >
                    {isUpdating === selectedTutorial.id ? (
                      <Loader2 className="w-3 h-3 md:w-4 md:h-4 animate-spin mr-1.5 md:mr-2" />
                    ) : (
                      <Circle className={`w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2 ${selectedTutorial.completed ? "fill-primary text-primary" : ""}`} />
                    )}
                    <span className="hidden sm:inline">Mark </span>Complete
                  </Button>
                )}
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="h-8 md:h-9 p-1 bg-muted/30">
                  <TabsTrigger value="notes" className="text-xs px-3 md:px-4">Notes</TabsTrigger>
                  <TabsTrigger value="course" className="text-xs px-3 md:px-4">Course</TabsTrigger>
                </TabsList>
                <TabsContent value="notes" className="mt-3 md:mt-4 text-xs md:text-sm text-muted-foreground leading-relaxed">
                  {selectedTutorial.description}
                  
                  {selectedTutorial.affiliateUrl && (
                    <div className="mt-4">
                      <Button variant="outline" size="sm" asChild className="text-xs md:text-sm">
                        <a href={selectedTutorial.affiliateUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                          Open {selectedTutorial.id === "openart-ai" ? "OpenArt" : selectedTutorial.id === "elevenlabs" ? "ElevenLabs" : "Hera AI"}
                        </a>
                      </Button>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="course" className="mt-3 md:mt-4 text-xs md:text-sm text-muted-foreground leading-relaxed">
                  <p>
                    These tutorials are designed to help you master every tool in the AI StoryCraft Studio.
                    Watch each video, practice the techniques, and mark them complete as you progress.
                  </p>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Lesson List - Show below on mobile */}
          <div className="lg:col-span-4 lg:order-1 space-y-3 md:space-y-4">
            {/* Progress Card */}
            <div className="bg-card/50 border border-border/50 rounded-xl p-3 md:p-4">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <span className="text-xs md:text-sm font-medium text-foreground">Progress</span>
                <span className="text-xs md:text-sm text-muted-foreground">{completedCount}/{tutorials.length} tutorials</span>
              </div>
              <Progress value={progressPercent} className="h-1.5 mb-2" />
              <p className="text-xs text-muted-foreground">
                {progressPercent}% complete. {progressPercent === 100 ? "Congratulations!" : "Keep it up."}
              </p>
            </div>

            {/* Tutorials List */}
            <div className="bg-card/50 border border-border/50 rounded-xl p-3 md:p-4">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="font-medium text-foreground text-xs md:text-sm">All Tutorials</h3>
                <span className="text-xs text-muted-foreground">{tutorials.length} videos</span>
              </div>

              <div className="space-y-0.5">
                {tutorials.map((tutorial) => {
                  const isActive = selectedTutorial.id === tutorial.id;
                  const isAvailable = !!tutorial.videoUrl;
                  return (
                    <div
                      key={tutorial.id}
                      onClick={() => setSelectedTutorial(tutorial)}
                      className={`flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2 md:py-2.5 rounded-lg cursor-pointer transition-all ${
                        isActive
                          ? "bg-primary/15 border-l-2 border-primary"
                          : "hover:bg-muted/30 active:bg-muted/40"
                      }`}
                    >
                      {/* Status icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isAvailable) toggleCompletion(tutorial.id);
                        }}
                        disabled={isUpdating === tutorial.id || !isAvailable}
                        className="flex-shrink-0 transition-transform hover:scale-110 disabled:opacity-50 p-1 -m-1"
                      >
                        {isUpdating === tutorial.id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        ) : tutorial.completed ? (
                          <CheckCircle className="w-4 h-4 text-primary" />
                        ) : isActive && isAvailable ? (
                          <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                            <Play className="w-2 h-2 text-primary-foreground ml-0.5" fill="currentColor" />
                          </div>
                        ) : (
                          <Circle className="w-4 h-4 text-muted-foreground/50" />
                        )}
                      </button>

                      {/* Title */}
                      <span className={`flex-1 text-xs md:text-sm line-clamp-1 ${isActive ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                        {tutorial.title}
                      </span>

                      {/* Duration */}
                      {isAvailable ? (
                        <span className="text-[10px] md:text-xs text-muted-foreground flex-shrink-0">{tutorial.duration}</span>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">Soon</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
